import { translations, type Locale } from './locales.js';

type StringKeyOf<T> = {
  [K in keyof T & string]: T[K] extends string
    ? K
    : T[K] extends { readonly one: string; readonly other: string }
      ? K
    : T[K] extends Record<string, unknown>
      ? `${K}.${StringKeyOf<T[K]>}`
      : never;
}[keyof T & string];

export type TranslationKey = StringKeyOf<(typeof translations)['pt-PT']>;
export type TranslationParams = Readonly<Record<string, string | number>>;

interface PluralMessage {
  readonly one: string;
  readonly other: string;
}

let activeLocale: Locale = 'pt-PT';

export function setLocale(locale: Locale): void {
  activeLocale = locale;
}

export function getLocale(): Locale {
  return activeLocale;
}

export function t(key: TranslationKey, params: TranslationParams = {}): string {
  const parts = key.split('.');
  let value: unknown = translations[activeLocale];
  for (const part of parts) {
    if (typeof value !== 'object' || value === null || !(part in value)) return key;
    value = (value as Record<string, unknown>)[part];
  }
  if (typeof value === 'object' && value !== null && 'one' in value && 'other' in value) {
    const count = params.count;
    if (typeof count !== 'number') return key;
    const category = new Intl.PluralRules(activeLocale).select(count);
    value = (value as PluralMessage)[category === 'one' ? 'one' : 'other'];
  }
  if (typeof value !== 'string') return key;
  return value.replace(/\{(\w+)\}/g, (match, name: string) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : match,
  );
}

export { en, ptPT, translations, type Locale } from './locales.js';
